// The user importer, as `user_import_actions` puts it on the wire. Rows travel
// as cells keyed by the importer's own column headers: a session exists to hold
// the rows that do not import cleanly yet.

import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';

export enum SessionStatus {
	Open = 'open',
	Staged = 'staged',
	Confirmed = 'confirmed',
	Reverted = 'reverted',
	Failed = 'failed',
}

export enum MappingSource {
	Exact = 'exact',
	Alias = 'alias',
	Ai = 'ai',
}

export interface MappingColumn {
	index: number;
	header: string;
	// Empty for a column the import ignores.
	target: string;
	source?: MappingSource;
}

export enum Severity {
	Blocking = 'blocking',
	Warning = 'warning',
}

export interface MappingProblem {
	code: string;
	severity: Severity;
	targets: string[];
}

export interface MappingProperty {
	target: string;
	group: string;
	tier: string;
	// Offered only to a column already named after it: these dispatch to an
	// update by ID.
	exactOnly: boolean;
	// Not a column of the importer: a full name that becomes first and last.
	transform: boolean;
}

export interface MappingResponse {
	columns: MappingColumn[];
	properties: MappingProperty[];
	problems: MappingProblem[];
	rowCount: number;
	delimiter: string;
	// Why the model was not asked, empty when it was.
	aiSkipped: string;
}

export interface CheckMappingResponse {
	problems: MappingProblem[];
}

export type DateOrder = '' | 'day_first' | 'month_first';

export interface DateOrderQuestion {
	column: string;
	examples: string[];
	default: DateOrder;
}

export interface FieldChange {
	column: string;
	old: string;
	new: string;
}

export interface MembershipPlan {
	group: string;
	groupId: number;
	role: string;
	// The role as written, when the club has none by that name.
	roleName: string;
	outcome: 'add' | 'existing' | 'unresolved' | 'ambiguous' | 'unknown_role';
}

export interface ParentCandidate {
	userId: number;
	firstName: string;
	lastName: string;
	email: string;
}

export interface ParentPlan {
	slot: number;
	outcome:
		| 'existing'
		| 'create'
		| 'created_above'
		| 'unresolved'
		| 'link_skipped';
	userId: number;
	// The same for every slot that lands on the same parent, so siblings group.
	key: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	alreadyLinked: boolean;
	candidates: ParentCandidate[];
}

export interface PossibleDuplicate {
	userId: number;
	line: number;
	firstName: string;
	lastName: string;
}

export type PlanAction =
	| 'create'
	| 'update'
	| 'unchanged'
	| 'rejected'
	| 'skipped';

export interface RowPlan {
	lines: number[];
	action: PlanAction;
	userId: number;
	matchedBy: '' | 'id' | 'personnummer' | 'email';
	isChild: boolean;
	changes: FieldChange[];
	memberships: MembershipPlan[];
	parents: ParentPlan[];
	possibleDuplicates: PossibleDuplicate[];
	reasons: string[];
}

export interface RowIssue {
	column: string;
	code: string;
	reason: string;
	value?: string;
}

export type Disposition = 'keep' | 'skip' | 'keep_as_new';

export interface SessionRow {
	id: number;
	line: number;
	cells: Record<string, string>;
	// What the file said, per mapped column.
	original: Record<string, string>;
	// The cells that are no longer what the file's column became, as imported:
	// edited by hand or rewritten by a clean-up.
	imported: Record<string, string>;
	disposition: Disposition;
	issues: RowIssue[];
	// Absent for a row set aside or one with issues.
	plan?: RowPlan;
	needsDecision: boolean;
}

export interface Preview {
	total: number;
	created: number;
	updated: number;
	unchanged: number;
	skipped: number;
	parentsToCreate: number;
	rowsWithIssues: number;
	needsDecision: number;
	excluded: number;
	canContinue: boolean;
}

export interface StoredColumn {
	index: number;
	header: string;
	target: string;
}

export interface SessionState {
	importId: string;
	name: string;
	status: SessionStatus;
	mapping: { columns: StoredColumn[]; dateOrder: DateOrder };
	rows: SessionRow[];
	preview: Preview;
}

export interface OpenSessionResponse {
	session?: SessionState;
	// Set instead of a session when a date column needs the user's word.
	dateOrderQuestion?: DateOrderQuestion;
}

export interface RevertStatus {
	status: string;
	deleted: number;
	restored: number;
	needsReview: number;
	error?: string;
}

export interface Status {
	status: string;
	total: number;
	created: number;
	updated: number;
	unchanged: number;
	rejected: number;
	warnings: string[];
	errors: string[];
	error?: string;
	revert?: RevertStatus;
}

export interface ListItem extends AllowedActionable {
	importId: string;
	name: string;
	status: SessionStatus;
	createdAt: number;
}

export interface Cleanup {
	id: string;
	// The importer's own column names the cleaning reads and may rewrite.
	columns: string[];
	// Rows with a value in those columns.
	rows: number;
	// What the model would be given.
	items: number;
	// Rows the importer refuses over one of the columns.
	issues: number;
	// The first items, exactly as the model would see them.
	examples: Record<string, string>[];
	recommended: boolean;
}

export interface CleanRef {
	id: number;
	line: number;
}

export interface CleanCell {
	column: string;
	from: string;
	to: string;
}

export interface CleanChange {
	rows: CleanRef[];
	cells: CleanCell[];
	// Widens what a person may do, such as an admin role.
	risky: boolean;
}

export interface CleanupProposal {
	id: string;
	changes: CleanChange[];
	unsure: number;
	// The first few values the AI could not match, as the file had them.
	unsureValues: string[];
	failed: number;
}

export interface CleanupListing {
	// Fixes the word lists make on their own.
	fixes: CleanupProposal[];
	// Cleanings left for the AI.
	cleanups: Cleanup[];
}

export interface CleanProposal {
	cleanups: CleanupProposal[];
	// Why the model was not asked, empty when it was.
	aiSkipped: string;
}

export enum CleanupRunStatus {
	Idle = 'idle',
	Running = 'running',
	Done = 'done',
	Failed = 'failed',
}

// Where an import's AI cleaning got to. It runs as a job, so the proposal is
// there once the status is done.
export interface CleanupRun {
	status: CleanupRunStatus;
	ids: string[];
	proposal?: CleanProposal;
}

export interface GroupChoice {
	name: string;
	// How far under the organization the group sits.
	depth: number;
}

export interface RoleChoice {
	name: string;
	// A role the club made, as opposed to player, staff or admin.
	custom: boolean;
}

export interface Choices {
	groups: GroupChoice[];
	roles: RoleChoice[];
}
